import { Component, ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { captureException } from "@/lib/sentry";

interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown) {
    console.error("App crashed:", error, info);
    captureException(error, {
      componentStack: info,
      boundary: "ErrorBoundary",
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="min-h-screen grid place-items-center px-6 bg-background text-foreground">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card p-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-destructive/15 grid place-items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h1 className="font-display font-bold text-xl mb-1">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mb-5">
            We hit an unexpected error. Try again, or contact support if it keeps happening.
          </p>
          <pre className="text-[10px] text-left bg-muted rounded-lg p-3 mb-5 overflow-auto max-h-32">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2 justify-center">
            <button
              onClick={() => { this.setState({ error: null }); location.assign("/"); }}
              className="px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold"
            >
              Go home
            </button>
            <a
              href="mailto:support@cashisle.app"
              className="px-4 py-2 rounded-xl border border-border text-sm font-semibold"
            >
              Email support
            </a>
          </div>
        </div>
      </main>
    );
  }
}
